/* Wizard Pratica di Credito Ayvens — unico form multi-step per
 * Privato / Micro-impresa / Impresa. I campi cambiano in modo condizionale.
 *
 * Step:
 *  1. Tipologia cliente
 *  2. Anagrafica (base)
 *  3. Dati specifici (impresa o privato)
 *  4. Referente + IBAN + Fatturazione
 *  5. Veicolo scelto (opzionale, passato via query string da CarDetailPage)
 *  6. Consensi + dichiarazioni
 *  7. Riepilogo + invio -> attesa YouSign
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ComuneAutocomplete from "@/components/common/ComuneAutocomplete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trackStep } from "@/utils/analytics";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft, ArrowRight, CheckCircle2, User, Building2, Briefcase, FileSignature,
  FileText, Info, ShieldCheck, CarFront, Mail, Phone, MapPin, CreditCard, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPOLOGIE = [
  { value: "privato",       label: "Privato",        desc: "Persona fisica (consumatore)",        Icon: User,      cfLen: 16 },
  { value: "micro_impresa", label: "Micro-impresa",  desc: "Max 10 dipendenti, fatturato ≤ €2M", Icon: Briefcase, cfLen: 11 },
  { value: "impresa",       label: "Impresa",        desc: "Società, SRL, SPA, partita IVA",     Icon: Building2, cfLen: 11 },
];

const STEPS = [
  { key: "tipologia",   label: "Tipologia" },
  { key: "anagrafica",  label: "Anagrafica" },
  { key: "dettagli",    label: "Dettagli" },
  { key: "referente",   label: "Referente" },
  { key: "veicolo",     label: "Veicolo" },
  { key: "consensi",    label: "Consensi" },
  { key: "riepilogo",   label: "Invio" },
];

const EMPTY_FORM = {
  tipologia: "",
  anagrafica: {
    denominazione: "", cf_piva: "", indirizzo: "", cap: "", citta: "", provincia: "",
    telefono: "", email: "", pec: "", iban: "",
  },
  fatturazione: {
    stesso_indirizzo: true, indirizzo: "", cap: "", citta: "", provincia: "", email: "", telefono: "",
  },
  referente: { titolo: "", nome: "", cognome: "", cf: "", telefono: "", email: "" },
  impresa: {
    forma_giuridica: "", num_rea: "", camera_commercio: "", settore_ateco: "",
    anno_costituzione: "", num_dipendenti: "", fatturato_annuo_eur: "",
    autodichiarazione_micro: null,
  },
  privato: {
    data_nascita: "", luogo_nascita: "", professione: "", reddito_mensile_eur: "",
    stato_civile: "", documento_tipo: "", documento_numero: "",
  },
  veicolo: {
    car_id: "", brand: "", model: "", canone_mensile: "", durata_mesi: "",
    km_annui: "", anticipo: "", note: "",
  },
  consensi: {
    // Consensi OBBLIGATORI precompilati per ridurre attrito (l'utente può deselezionare).
    // I FACOLTATIVI (servizi_aggiuntivi, marketing) restano OFF in conformità GDPR/EDPB.
    privacy: true, condizioni_precontrattuali: true, informativa_sic: true,
    stato_uso_dichiarazione: true, servizi_aggiuntivi: false, marketing: false,
  },
  note: "",
};

export default function PraticaCreditoPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [stepIdx, setStepIdx] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Analytics funnel credito_pratica
  useEffect(() => {
    const stepName = STEPS[stepIdx]?.key ? `step_${STEPS[stepIdx].key}` : (stepIdx === 0 ? "start" : `step_${stepIdx}`);
    trackStep("credito_pratica", stepIdx === 0 ? "start" : stepName);
  }, [stepIdx]);

  // Auto-fill veicolo da query string (?car=id&brand=Fiat&model=Panda&...)
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (q.get("car") || q.get("brand")) {
      setForm((f) => ({
        ...f,
        veicolo: {
          ...f.veicolo,
          car_id: q.get("car") || "",
          brand: q.get("brand") || "",
          model: q.get("model") || "",
          canone_mensile: q.get("canone") || "",
          durata_mesi: q.get("durata") || "",
          km_annui: q.get("km") || "",
          anticipo: q.get("anticipo") || "",
        },
      }));
    }
  }, [location.search]);

  const step = STEPS[stepIdx];
  const isImpresa = form.tipologia === "impresa" || form.tipologia === "micro_impresa";
  const isMicro = form.tipologia === "micro_impresa";
  const tipologiaMeta = TIPOLOGIE.find((t) => t.value === form.tipologia);

  const updateField = (section, field, value) => {
    setForm((f) => ({ ...f, [section]: { ...f[section], [field]: value } }));
  };

  // ========= Validation per step =========
  const canAdvance = useMemo(() => {
    if (step.key === "tipologia") return !!form.tipologia;
    if (step.key === "anagrafica") {
      const a = form.anagrafica;
      const cfOk = tipologiaMeta ? a.cf_piva.replace(/\s/g, "").length === tipologiaMeta.cfLen : false;
      return a.denominazione.length >= 2 && cfOk && a.indirizzo && /^\d{5}$/.test(a.cap) &&
        a.citta && /^[A-Za-z]{2}$/.test(a.provincia) && a.telefono && /@/.test(a.email) && a.iban.length >= 15;
    }
    if (step.key === "dettagli") {
      if (isImpresa) {
        return !!form.impresa.forma_giuridica;
      }
      return true; // privato: tutto opzionale
    }
    if (step.key === "referente") {
      const r = form.referente;
      return r.nome && r.cognome && /^[A-Z0-9]{16}$/i.test(r.cf) && r.telefono && /@/.test(r.email);
    }
    if (step.key === "veicolo") return true; // sempre opzionale
    if (step.key === "consensi") {
      const c = form.consensi;
      if (isMicro && form.impresa.autodichiarazione_micro === null) return false;
      return c.privacy && c.condizioni_precontrattuali && c.informativa_sic && c.stato_uso_dichiarazione;
    }
    return true;
  }, [step, form, tipologiaMeta, isImpresa, isMicro]);

  const next = () => {
    // salto "dettagli" se tipologia non ha nulla di specifico
    setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
  };
  const prev = () => setStepIdx((i) => Math.max(0, i - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        tipologia: form.tipologia,
        anagrafica: {
          ...form.anagrafica,
          cf_piva: form.anagrafica.cf_piva.toUpperCase().replace(/\s/g, ""),
          iban: form.anagrafica.iban.toUpperCase().replace(/\s/g, ""),
          provincia: form.anagrafica.provincia.toUpperCase(),
        },
        fatturazione: form.fatturazione,
        referente: { ...form.referente, cf: form.referente.cf.toUpperCase() },
        impresa: isImpresa ? {
          ...form.impresa,
          anno_costituzione: form.impresa.anno_costituzione ? Number(form.impresa.anno_costituzione) : null,
          num_dipendenti: form.impresa.num_dipendenti ? Number(form.impresa.num_dipendenti) : null,
          fatturato_annuo_eur: form.impresa.fatturato_annuo_eur ? Number(form.impresa.fatturato_annuo_eur) : null,
          autodichiarazione_micro: isMicro ? form.impresa.autodichiarazione_micro : null,
        } : null,
        privato: form.tipologia === "privato" ? {
          ...form.privato,
          reddito_mensile_eur: form.privato.reddito_mensile_eur ? Number(form.privato.reddito_mensile_eur) : null,
        } : null,
        veicolo: form.veicolo.car_id || form.veicolo.brand ? {
          ...form.veicolo,
          canone_mensile: form.veicolo.canone_mensile ? Number(form.veicolo.canone_mensile) : null,
          durata_mesi: form.veicolo.durata_mesi ? Number(form.veicolo.durata_mesi) : null,
          km_annui: form.veicolo.km_annui ? Number(form.veicolo.km_annui) : null,
          anticipo: form.veicolo.anticipo ? Number(form.veicolo.anticipo) : null,
        } : null,
        consensi: form.consensi,
        note: form.note,
      };
      const { data } = await axios.post(`${API}/credito/pratica`, payload);
      setResult(data);
      toast.success("Pratica inviata correttamente");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio pratica");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) return <SuccessView result={result} />;

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="credito-page">
        {/* Hero */}
        <section className="relative bg-gradient-to-br from-[#0F1F14] via-[#163321] to-[#1d4a2e] text-white overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-[#D97B58]/10 blur-3xl" />
          <div className="relative max-w-4xl mx-auto px-5 sm:px-6 py-12">
            <Link to="/noleggio" className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white mb-4 transition-colors" data-testid="back-to-catalog">
              <ArrowLeft className="w-3.5 h-3.5" /> Catalogo Noleggio
            </Link>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-[10px] font-semibold uppercase tracking-wider text-[#D97B58] mb-3">
              <FileSignature className="w-3 h-3" /> Pratica di credito Ayvens
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold leading-tight mb-2">
              Un unico <span className="text-[#D97B58]">questionario guidato</span>
            </h1>
            <p className="text-sm text-white/80 max-w-2xl leading-relaxed">
              Rispondi ai campi essenziali per Privati, Micro-imprese o Imprese. I moduli Ayvens (MLA Consumer/Business, autodichiarazione micro, informative SIC) vengono compilati in automatico dai tuoi dati, con firma elettronica YouSign al termine.
            </p>
          </div>
        </section>

        {/* Stepper */}
        <div className="max-w-4xl mx-auto px-5 sm:px-6 py-6">
          <div className="flex items-center gap-1 flex-wrap mb-6" data-testid="step-indicator">
            {STEPS.map((s, i) => {
              const done = i < stepIdx;
              const active = i === stepIdx;
              return (
                <div key={s.key} className="flex items-center flex-1 min-w-[60px]">
                  <div className={`h-8 min-w-8 px-2 rounded-full inline-flex items-center justify-center text-[10px] font-bold uppercase tracking-wide border transition-colors ${
                    done ? "bg-[#163321] text-white border-[#163321]" :
                    active ? "bg-[#D97B58] text-white border-[#D97B58]" :
                    "bg-white text-[#4A5E51] border-[#E5E2DB]"
                  }`}>
                    {done ? <CheckCircle2 className="w-3 h-3" /> : i + 1}
                    <span className="hidden sm:inline ml-1">{s.label}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${done ? "bg-[#163321]" : "bg-[#E5E2DB]"}`} />}
                </div>
              );
            })}
          </div>

          <div className="bg-white rounded-2xl border border-[#E5E2DB] p-6 sm:p-8 shadow-sm" data-testid={`step-${step.key}`}>
            {step.key === "tipologia" && <StepTipologia form={form} setForm={setForm} />}
            {step.key === "anagrafica" && <StepAnagrafica form={form} update={updateField} tipologiaMeta={tipologiaMeta} setForm={setForm} />}
            {step.key === "dettagli" && <StepDettagli form={form} update={updateField} isImpresa={isImpresa} isMicro={isMicro} />}
            {step.key === "referente" && <StepReferente form={form} update={updateField} />}
            {step.key === "veicolo" && <StepVeicolo form={form} update={updateField} />}
            {step.key === "consensi" && <StepConsensi form={form} update={updateField} isMicro={isMicro} setForm={setForm} />}
            {step.key === "riepilogo" && <StepRiepilogo form={form} tipologiaMeta={tipologiaMeta} />}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <Button variant="outline" onClick={prev} disabled={stepIdx === 0} className="rounded-full" data-testid="btn-prev">
              <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
            </Button>
            {step.key === "riepilogo" ? (
              <Button onClick={submit} disabled={submitting || !canAdvance} className="rounded-full bg-[#D97B58] hover:bg-[#c56a48] text-white" data-testid="btn-submit">
                {submitting ? "Invio..." : "Invia pratica"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={next} disabled={!canAdvance} className="rounded-full bg-[#163321] hover:bg-[#1d4a2e] text-white" data-testid="btn-next">
                Avanti <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

// ============================= STEPS =============================

function StepTipologia({ form, setForm }) {
  return (
    <div>
      <h2 className="font-heading text-xl text-[#0F1F14] font-medium mb-1">Chi sta richiedendo il credito?</h2>
      <p className="text-sm text-[#4A5E51] mb-6">Il questionario si adatta automaticamente ai campi necessari.</p>
      <div className="grid gap-3 sm:grid-cols-3">
        {TIPOLOGIE.map((t) => {
          const Icon = t.Icon;
          const active = form.tipologia === t.value;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => setForm((f) => ({ ...f, tipologia: t.value }))}
              className={`text-left p-5 rounded-xl border-2 transition-all ${active ? "border-[#D97B58] bg-[#D97B58]/5" : "border-[#E5E2DB] hover:border-[#163321]/40"}`}
              data-testid={`tipologia-${t.value}`}
            >
              <Icon className={`w-7 h-7 mb-3 ${active ? "text-[#D97B58]" : "text-[#163321]"}`} />
              <p className="font-heading font-semibold text-[#0F1F14] mb-1">{t.label}</p>
              <p className="text-xs text-[#4A5E51] leading-relaxed">{t.desc}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepAnagrafica({ form, update, tipologiaMeta, setForm }) {
  const a = form.anagrafica;
  const isPrivato = form.tipologia === "privato";
  return (
    <div>
      <h2 className="font-heading text-xl text-[#0F1F14] font-medium mb-1">Anagrafica {isPrivato ? "personale" : "azienda"}</h2>
      <p className="text-sm text-[#4A5E51] mb-4">Questi dati popolano il modulo MLA Ayvens e i successivi documenti.</p>

      {/* Auto-estrazione AI dai documenti */}
      <AutoExtractBox form={form} setForm={setForm} />

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={isPrivato ? "Nome e Cognome" : "Ragione sociale"} required>
          <Input data-testid="field-denominazione" value={a.denominazione} onChange={(e) => update("anagrafica", "denominazione", e.target.value)} placeholder={isPrivato ? "Mario Rossi" : "Esempio S.r.l."} />
        </Field>
        <Field label={isPrivato ? `Codice Fiscale (${tipologiaMeta?.cfLen || 16} car.)` : `P.IVA (${tipologiaMeta?.cfLen || 11} cifre)`} required>
          <Input data-testid="field-cfpiva" value={a.cf_piva} onChange={(e) => update("anagrafica", "cf_piva", e.target.value.toUpperCase())} maxLength={tipologiaMeta?.cfLen || 16} placeholder={isPrivato ? "RSSMRA80A01H501Z" : "12345678901"} />
        </Field>
        <Field label="Indirizzo" required wide>
          <Input data-testid="field-indirizzo" value={a.indirizzo} onChange={(e) => update("anagrafica", "indirizzo", e.target.value)} placeholder="Via Roma 1" />
        </Field>
        <Field label="CAP" required>
          <Input data-testid="field-cap" value={a.cap} onChange={(e) => update("anagrafica", "cap", e.target.value.replace(/\D/g, "").slice(0, 5))} maxLength={5} placeholder="80100" />
        </Field>
        <Field label="Città" required>
          <Input data-testid="field-citta" value={a.citta} onChange={(e) => update("anagrafica", "citta", e.target.value)} placeholder="Napoli" />
        </Field>
        <Field label="Provincia" required>
          <Input data-testid="field-provincia" value={a.provincia} onChange={(e) => update("anagrafica", "provincia", e.target.value.toUpperCase().slice(0, 2))} maxLength={2} placeholder="NA" />
        </Field>
        <Field label="Telefono" required>
          <Input data-testid="field-telefono" value={a.telefono} onChange={(e) => update("anagrafica", "telefono", e.target.value)} placeholder="+39 081 1234567" />
        </Field>
        <Field label="Email" required>
          <Input data-testid="field-email" type="email" value={a.email} onChange={(e) => update("anagrafica", "email", e.target.value)} placeholder="nome@esempio.it" />
        </Field>
        {!isPrivato && (
          <Field label="PEC (se disponibile)">
            <Input data-testid="field-pec" type="email" value={a.pec} onChange={(e) => update("anagrafica", "pec", e.target.value)} placeholder="azienda@pec.it" />
          </Field>
        )}
        <Field label="IBAN" required wide>
          <Input data-testid="field-iban" value={a.iban} onChange={(e) => update("anagrafica", "iban", e.target.value.toUpperCase().replace(/\s/g, ""))} maxLength={34} placeholder="IT60 X054 2811 1010 0000 0123 456" />
        </Field>
      </div>
    </div>
  );
}

function StepDettagli({ form, update, isImpresa, isMicro }) {
  if (isImpresa) {
    const i = form.impresa;
    return (
      <div>
        <h2 className="font-heading text-xl text-[#0F1F14] font-medium mb-1">Dati aziendali</h2>
        <p className="text-sm text-[#4A5E51] mb-6">Informazioni utilizzate per la valutazione creditizia da parte di Ayvens.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Forma giuridica" required>
            <Select value={i.forma_giuridica} onValueChange={(v) => update("impresa", "forma_giuridica", v)}>
              <SelectTrigger data-testid="field-forma-giuridica"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Ditta Individuale">Ditta Individuale</SelectItem>
                <SelectItem value="SNC">SNC</SelectItem>
                <SelectItem value="SAS">SAS</SelectItem>
                <SelectItem value="SRL">SRL</SelectItem>
                <SelectItem value="SRLS">SRLS</SelectItem>
                <SelectItem value="SPA">SPA</SelectItem>
                <SelectItem value="SAPA">SAPA</SelectItem>
                <SelectItem value="Cooperativa">Cooperativa</SelectItem>
                <SelectItem value="Studio Professionale">Studio Professionale</SelectItem>
                <SelectItem value="Ente/Associazione">Ente/Associazione</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Numero REA">
            <Input data-testid="field-rea" value={i.num_rea} onChange={(e) => update("impresa", "num_rea", e.target.value)} placeholder="NA-123456" />
          </Field>
          <Field label="Camera di Commercio">
            <Input data-testid="field-cciaa" value={i.camera_commercio} onChange={(e) => update("impresa", "camera_commercio", e.target.value)} placeholder="Napoli" />
          </Field>
          <Field label="Codice ATECO">
            <Input data-testid="field-ateco" value={i.settore_ateco} onChange={(e) => update("impresa", "settore_ateco", e.target.value)} placeholder="62.01.00" />
          </Field>
          <Field label="Anno costituzione">
            <Input data-testid="field-anno" type="number" value={i.anno_costituzione} onChange={(e) => update("impresa", "anno_costituzione", e.target.value)} placeholder="2015" />
          </Field>
          <Field label="Numero dipendenti">
            <Input data-testid="field-dip" type="number" value={i.num_dipendenti} onChange={(e) => update("impresa", "num_dipendenti", e.target.value)} placeholder="5" />
          </Field>
          <Field label="Fatturato annuo (€)" wide>
            <Input data-testid="field-fatturato" type="number" value={i.fatturato_annuo_eur} onChange={(e) => update("impresa", "fatturato_annuo_eur", e.target.value)} placeholder="150000" />
          </Field>
        </div>
        {isMicro && (
          <div className="mt-6 bg-[#FFF8E1] border border-[#E8D3A0] rounded-xl p-5" data-testid="autodichiarazione-micro-box">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[#8B6100] mb-2 flex items-center gap-1">
              <FileText className="w-3 h-3" /> Autodichiarazione Micro-impresa
            </p>
            <p className="text-xs text-[#0F1F14] leading-relaxed mb-3">
              Ai sensi della Raccomandazione UE 2003/361, si definisce <strong>micro-impresa</strong> un'impresa con meno di 10 dipendenti e un fatturato/bilancio annuo non superiore a 2 milioni di euro. Seleziona la tua situazione:
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${i.autodichiarazione_micro === true ? "border-[#163321] bg-white" : "border-[#E5E2DB]"}`}>
                <Checkbox checked={i.autodichiarazione_micro === true} onCheckedChange={() => update("impresa", "autodichiarazione_micro", true)} data-testid="autodich-micro-yes" />
                <span className="text-xs text-[#0F1F14]">Dichiaro di <strong>appartenere</strong> alla categoria delle Micro-imprese</span>
              </label>
              <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${i.autodichiarazione_micro === false ? "border-[#163321] bg-white" : "border-[#E5E2DB]"}`}>
                <Checkbox checked={i.autodichiarazione_micro === false} onCheckedChange={() => update("impresa", "autodichiarazione_micro", false)} data-testid="autodich-micro-no" />
                <span className="text-xs text-[#0F1F14]">Dichiaro di <strong>non appartenere</strong> alla categoria</span>
              </label>
            </div>
          </div>
        )}
      </div>
    );
  }
  // Privato
  const p = form.privato;
  return (
    <div>
      <h2 className="font-heading text-xl text-[#0F1F14] font-medium mb-1">Dati personali</h2>
      <p className="text-sm text-[#4A5E51] mb-6">Queste informazioni aiutano Ayvens nella valutazione del merito creditizio.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Data di nascita">
          <Input data-testid="field-data-nascita" type="date" value={p.data_nascita} onChange={(e) => update("privato", "data_nascita", e.target.value)} />
        </Field>
        <Field label="Luogo di nascita">
          <ComuneAutocomplete
            value={p.luogo_nascita}
            onChange={(v) => update("privato", "luogo_nascita", v)}
            placeholder="Napoli"
          />
        </Field>
        <Field label="Professione">
          <Input data-testid="field-professione" value={p.professione} onChange={(e) => update("privato", "professione", e.target.value)} placeholder="Impiegato" />
        </Field>
        <Field label="Reddito mensile netto (€)">
          <Input data-testid="field-reddito" type="number" value={p.reddito_mensile_eur} onChange={(e) => update("privato", "reddito_mensile_eur", e.target.value)} placeholder="2000" />
        </Field>
        <Field label="Stato civile">
          <Select value={p.stato_civile} onValueChange={(v) => update("privato", "stato_civile", v)}>
            <SelectTrigger data-testid="field-stato-civile"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="celibe_nubile">Celibe/Nubile</SelectItem>
              <SelectItem value="coniugato">Coniugato/a</SelectItem>
              <SelectItem value="separato">Separato/a</SelectItem>
              <SelectItem value="divorziato">Divorziato/a</SelectItem>
              <SelectItem value="vedovo">Vedovo/a</SelectItem>
              <SelectItem value="unione_civile">Unione civile</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Tipo documento">
          <Select value={p.documento_tipo} onValueChange={(v) => update("privato", "documento_tipo", v)}>
            <SelectTrigger data-testid="field-doc-tipo"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="CI">Carta d'Identità</SelectItem>
              <SelectItem value="Patente">Patente</SelectItem>
              <SelectItem value="Passaporto">Passaporto</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Numero documento" wide>
          <Input data-testid="field-doc-numero" value={p.documento_numero} onChange={(e) => update("privato", "documento_numero", e.target.value)} placeholder="AB1234567" />
        </Field>
      </div>
    </div>
  );
}

function StepReferente({ form, update }) {
  const r = form.referente;
  const f = form.fatturazione;
  return (
    <div>
      <h2 className="font-heading text-xl text-[#0F1F14] font-medium mb-1">Referente & Fatturazione</h2>
      <p className="text-sm text-[#4A5E51] mb-6">Persona di contatto per comunicazioni e firma elettronica.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Titolo">
          <Select value={r.titolo} onValueChange={(v) => update("referente", "titolo", v)}>
            <SelectTrigger data-testid="field-ref-titolo"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Sig.">Sig.</SelectItem>
              <SelectItem value="Sig.ra">Sig.ra</SelectItem>
              <SelectItem value="Dott.">Dott.</SelectItem>
              <SelectItem value="Dott.ssa">Dott.ssa</SelectItem>
              <SelectItem value="Ing.">Ing.</SelectItem>
              <SelectItem value="Avv.">Avv.</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Nome" required>
          <Input data-testid="field-ref-nome" value={r.nome} onChange={(e) => update("referente", "nome", e.target.value)} />
        </Field>
        <Field label="Cognome" required>
          <Input data-testid="field-ref-cognome" value={r.cognome} onChange={(e) => update("referente", "cognome", e.target.value)} />
        </Field>
        <Field label="Codice Fiscale referente (16 car.)" required>
          <Input data-testid="field-ref-cf" value={r.cf} onChange={(e) => update("referente", "cf", e.target.value.toUpperCase())} maxLength={16} />
        </Field>
        <Field label="Telefono" required>
          <Input data-testid="field-ref-telefono" value={r.telefono} onChange={(e) => update("referente", "telefono", e.target.value)} />
        </Field>
        <Field label="Email" required>
          <Input data-testid="field-ref-email" type="email" value={r.email} onChange={(e) => update("referente", "email", e.target.value)} />
        </Field>
      </div>

      {/* Fatturazione */}
      <div className="mt-8 pt-6 border-t border-[#E5E2DB]">
        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <Checkbox
            checked={f.stesso_indirizzo}
            onCheckedChange={(v) => update("fatturazione", "stesso_indirizzo", !!v)}
            data-testid="fatt-stesso-indirizzo"
          />
          <span className="text-sm text-[#0F1F14]">Usa lo stesso indirizzo dell'anagrafica per la fatturazione</span>
        </label>
        {!f.stesso_indirizzo && (
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Indirizzo fatturazione" required wide>
              <Input data-testid="field-fatt-indirizzo" value={f.indirizzo} onChange={(e) => update("fatturazione", "indirizzo", e.target.value)} />
            </Field>
            <Field label="CAP" required>
              <Input data-testid="field-fatt-cap" value={f.cap} onChange={(e) => update("fatturazione", "cap", e.target.value.replace(/\D/g, "").slice(0, 5))} maxLength={5} />
            </Field>
            <Field label="Città" required>
              <Input data-testid="field-fatt-citta" value={f.citta} onChange={(e) => update("fatturazione", "citta", e.target.value)} />
            </Field>
            <Field label="Provincia" required>
              <Input data-testid="field-fatt-provincia" value={f.provincia} onChange={(e) => update("fatturazione", "provincia", e.target.value.toUpperCase().slice(0, 2))} maxLength={2} />
            </Field>
            <Field label="Email fatturazione">
              <Input data-testid="field-fatt-email" type="email" value={f.email} onChange={(e) => update("fatturazione", "email", e.target.value)} />
            </Field>
            <Field label="Telefono fatturazione">
              <Input data-testid="field-fatt-telefono" value={f.telefono} onChange={(e) => update("fatturazione", "telefono", e.target.value)} />
            </Field>
          </div>
        )}
      </div>
    </div>
  );
}

function StepVeicolo({ form, update }) {
  const v = form.veicolo;
  return (
    <div>
      <h2 className="font-heading text-xl text-[#0F1F14] font-medium mb-1">Veicolo (opzionale)</h2>
      <p className="text-sm text-[#4A5E51] mb-6">
        Se hai già scelto un'auto dal nostro catalogo i dati sono precompilati. Altrimenti compila manualmente o lascia vuoto — il consulente completerà.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Brand">
          <Input data-testid="field-v-brand" value={v.brand} onChange={(e) => update("veicolo", "brand", e.target.value)} placeholder="Fiat" />
        </Field>
        <Field label="Modello">
          <Input data-testid="field-v-model" value={v.model} onChange={(e) => update("veicolo", "model", e.target.value)} placeholder="Panda" />
        </Field>
        <Field label="Canone mensile (€)">
          <Input data-testid="field-v-canone" type="number" value={v.canone_mensile} onChange={(e) => update("veicolo", "canone_mensile", e.target.value)} placeholder="249" />
        </Field>
        <Field label="Durata (mesi)">
          <Input data-testid="field-v-durata" type="number" value={v.durata_mesi} onChange={(e) => update("veicolo", "durata_mesi", e.target.value)} placeholder="48" />
        </Field>
        <Field label="Km annui">
          <Input data-testid="field-v-km" type="number" value={v.km_annui} onChange={(e) => update("veicolo", "km_annui", e.target.value)} placeholder="10000" />
        </Field>
        <Field label="Anticipo (€)">
          <Input data-testid="field-v-anticipo" type="number" value={v.anticipo} onChange={(e) => update("veicolo", "anticipo", e.target.value)} placeholder="0" />
        </Field>
        <Field label="Note" wide>
          <Textarea data-testid="field-v-note" value={v.note} onChange={(e) => update("veicolo", "note", e.target.value)} rows={2} placeholder="Allestimento, colore, richieste particolari..." />
        </Field>
      </div>
    </div>
  );
}

function StepConsensi({ form, update, setForm }) {
  const c = form.consensi;
  const items = [
    { key: "privacy", required: true, label: "Privacy (D.Lgs. 196/2003 e GDPR)",
      desc: "Ho letto e accetto l'informativa sul trattamento dei dati personali da parte di Colucci Assicuratori e Ayvens S.p.A.",
      pdf: null, internalLink: "/privacy" },
    { key: "condizioni_precontrattuali", required: true, label: "Informazioni precontrattuali Ayvens",
      desc: "Ho ricevuto e accetto il documento \"Informazioni precontrattuali\" relativo al contratto di locazione operativa.",
      pdf: "precontrattuali" },
    { key: "informativa_sic", required: true, label: "Informativa scoring SIC",
      desc: "Autorizzo Ayvens al trattamento dei dati presso i Sistemi di Informazioni Creditizie per la valutazione del merito creditizio.",
      pdf: "sic" },
    { key: "stato_uso_dichiarazione", required: true, label: "Dichiarazione di guida e stato d'uso",
      desc: "Dichiaro di essere consapevole degli obblighi relativi alla custodia, guida e stato d'uso del veicolo Ayvens.",
      pdf: "guida" },
    { key: "servizi_aggiuntivi", required: false, label: "Modulo servizi aggiuntivi (opzionale)",
      desc: "Accetto il modulo servizi aggiuntivi Ayvens (auto sostitutiva, veicolo in più, danni da responsabilità civile terzi, ecc.).",
      pdf: "mod_serv_agg" },
    { key: "marketing", required: false, label: "Marketing e comunicazioni commerciali (opzionale)",
      desc: "Acconsento a ricevere comunicazioni commerciali e promozionali da Colucci Assicuratori.",
      pdf: null },
  ];
  const pdfUrl = (key) => `${API}/credito/informative/${key}`;
  return (
    <div>
      <h2 className="font-heading text-xl text-[#0F1F14] font-medium mb-1">Consensi e dichiarazioni</h2>
      <p className="text-sm text-[#4A5E51] mb-3">
        I primi quattro sono obbligatori per inviare la pratica. <strong>Scarica e leggi ogni informativa</strong> prima di dare il consenso. La firma elettronica sui moduli ufficiali avverrà in un secondo momento tramite YouSign.
      </p>
      <div className="mb-5 p-3 rounded-xl bg-[#F5F8F6] border border-[#163321]/10 text-[11px] text-[#4A5E51] flex items-start gap-2" data-testid="precompilato-hint-credito">
        <span className="font-semibold text-[#163321]">✓ Informative precompilate</span>
        <span>— Per velocizzare la compilazione, i consensi obbligatori sono già spuntati. Puoi deselezionare singolarmente se non sei d'accordo. I facoltativi (Servizi aggiuntivi, Marketing) restano disattivati in conformità GDPR.</span>
      </div>
      <div className="space-y-3">
        {items.map((it) => (
          <div key={it.key} className={`p-4 rounded-xl border transition-colors ${c[it.key] ? "border-[#163321] bg-[#F5F8F6]" : "border-[#E5E2DB]"}`}>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={!!c[it.key]}
                onCheckedChange={(v) => update("consensi", it.key, !!v)}
                data-testid={`consenso-${it.key}`}
                className="mt-0.5"
              />
              <div className="flex-1">
                <p className="text-sm font-semibold text-[#0F1F14] mb-0.5">
                  {it.label}
                  {it.required && <span className="text-[#D97B58] ml-1">*</span>}
                </p>
                <p className="text-xs text-[#4A5E51] leading-relaxed">{it.desc}</p>
              </div>
            </label>
            {(it.pdf || it.internalLink) && (
              <div className="mt-2 pl-8 flex items-center gap-3 flex-wrap">
                {it.pdf && (
                  <a
                    href={pdfUrl(it.pdf)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-[11px] text-[#163321] hover:text-[#D97B58] font-semibold transition-colors"
                    data-testid={`pdf-${it.key}`}
                  >
                    <FileText className="w-3.5 h-3.5" /> Leggi il PDF ufficiale
                  </a>
                )}
                {it.internalLink && (
                  <a
                    href={it.internalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-[11px] text-[#163321] hover:text-[#D97B58] font-semibold transition-colors"
                  >
                    <FileText className="w-3.5 h-3.5" /> Leggi l'informativa
                  </a>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Tutti i documenti scaricabili (info box) organizzati per gruppo */}
      <TuttiDocumentiBox tipologia={form.tipologia} form={form} />

      <div className="mt-6 pt-6 border-t border-[#E5E2DB]">
        <Label className="text-xs font-semibold text-[#0F1F14] mb-2 block">Note o richieste particolari (opzionale)</Label>
        <Textarea
          data-testid="field-note"
          value={form.note}
          onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
          rows={3}
          placeholder="Es. preferisco essere ricontattato la mattina, ho già un finanziamento in corso, ecc."
        />
      </div>
    </div>
  );
}

function TuttiDocumentiBox({ tipologia, form }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/credito/informative`, {
          params: tipologia ? { tipologia } : {},
        });
        if (!aborted) setItems(data?.informative || []);
      } catch {
        /* silently */
      }
    })();
    return () => { aborted = true; };
  }, [tipologia]);

  if (items.length === 0) return null;
  const groups = {
    informative: { label: "Informative e dichiarazioni", items: [] },
    guide_riconsegna: { label: "Guide riconsegna veicolo", items: [] },
    moduli: { label: "Moduli operativi", items: [] },
    mla: { label: "Fac-simile contratto MLA", items: [] },
  };
  items.forEach((it) => {
    const g = groups[it.group] || groups.informative;
    g.items.push(it);
  });

  // Payload precompilazione live: usa i dati che il cliente ha già inserito nel wizard
  const precompilaPayload = {
    tipologia: form?.tipologia,
    anagrafica: form?.anagrafica,
    referente: form?.referente,
    fatturazione: form?.fatturazione,
  };
  const precompiledCount = items.filter(i => i.precompilable).length;

  return (
    <div className="mt-6 bg-[#FFF7E8] border border-[#E8D3A0] rounded-xl p-4" data-testid="informative-download-box">
      <p className="text-[10px] uppercase tracking-widest font-bold text-[#8B6100] mb-2 flex items-center gap-1.5">
        <FileSignature className="w-3 h-3" /> Tutti i documenti Ayvens ({items.length})
      </p>
      <p className="text-xs text-[#8B6100] leading-relaxed mb-3">
        <strong>{precompiledCount} moduli</strong> sono <strong>precompilati con i tuoi dati</strong> in tempo reale. Le brochure informative (riconsegna, emergenze, ecc.) sono materiale di lettura.
      </p>
      <div className="space-y-3">
        {Object.entries(groups).map(([k, g]) => (
          g.items.length === 0 ? null : (
            <div key={k}>
              <p className="text-[9px] uppercase tracking-wider font-bold text-[#8B6100]/70 mb-1.5">{g.label}</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {g.items.map((it) => (
                  <PdfLink
                    key={it.key}
                    docKey={it.key}
                    staticUrl={`${API}${it.url}`}
                    precompilaUrl={`${API}${it.precompila_url}`}
                    precompilable={it.precompilable}
                    payload={precompilaPayload}
                    label={it.label}
                    isSpecific={it.specific_for?.length > 0}
                  />
                ))}
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

function PdfLink({ docKey, staticUrl, precompilaUrl, precompilable, payload, label, isSpecific }) {
  const [loading, setLoading] = useState(false);

  const onClick = async (e) => {
    if (!precompilable) return; // <a href> static fallback per gli informativi
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const res = await axios.post(precompilaUrl, payload, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank", "noopener");
      // Revoke dopo 60s per consentire al browser di caricare
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      // fallback su versione statica se la precompilazione fallisce
      window.open(staticUrl, "_blank", "noopener");
    } finally {
      setLoading(false);
    }
  };

  return (
    <a
      href={staticUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className="flex items-center gap-2 p-2 rounded-lg bg-white border border-[#E8D3A0]/60 hover:border-[#8B6100] transition-colors text-[11px] text-[#8B6100] hover:text-[#0F1F14]"
      title={label}
      data-testid={`pdf-link-${docKey}`}
    >
      <FileText className="w-3.5 h-3.5 shrink-0" />
      <span className="truncate flex-1">{label}</span>
      {precompilable && (
        <span className="text-[8px] uppercase text-[#163321] bg-[#163321]/10 px-1.5 py-0.5 rounded font-bold" title="Precompilato con i tuoi dati">
          {loading ? "..." : "Precomp."}
        </span>
      )}
      {isSpecific && !precompilable && <span className="text-[8px] uppercase text-[#D97B58] font-bold">Imp</span>}
    </a>
  );
}

function StepRiepilogo({ form, tipologiaMeta }) {
  const a = form.anagrafica;
  const r = form.referente;
  const v = form.veicolo;
  return (
    <div>
      <h2 className="font-heading text-xl text-[#0F1F14] font-medium mb-1">Riepilogo pratica</h2>
      <p className="text-sm text-[#4A5E51] mb-6">Controlla i dati inseriti. Cliccando "Invia pratica" i dati vengono trasmessi in modalità cifrata ai nostri consulenti.</p>
      <div className="space-y-4">
        <SummaryBox title="Tipologia" icon={tipologiaMeta?.Icon || Briefcase}>
          <p className="text-sm"><strong>{tipologiaMeta?.label || "—"}</strong> — {tipologiaMeta?.desc || ""}</p>
        </SummaryBox>
        <SummaryBox title="Anagrafica" icon={User}>
          <p className="text-sm"><strong>{a.denominazione}</strong> · {a.cf_piva}</p>
          <p className="text-xs text-[#4A5E51]">{a.indirizzo}, {a.cap} {a.citta} ({a.provincia})</p>
          <p className="text-xs text-[#4A5E51] flex items-center gap-2 mt-1"><Mail className="w-3 h-3" />{a.email} · <Phone className="w-3 h-3" />{a.telefono}</p>
          <p className="text-xs text-[#4A5E51] mt-1 flex items-center gap-1"><CreditCard className="w-3 h-3" />IBAN: {a.iban}</p>
        </SummaryBox>
        <SummaryBox title="Referente" icon={User}>
          <p className="text-sm">{r.titolo} {r.nome} {r.cognome} · CF {r.cf}</p>
          <p className="text-xs text-[#4A5E51]">{r.email} · {r.telefono}</p>
        </SummaryBox>
        {(v.brand || v.model) && (
          <SummaryBox title="Veicolo" icon={CarFront}>
            <p className="text-sm"><strong>{v.brand} {v.model}</strong></p>
            <p className="text-xs text-[#4A5E51]">
              {v.canone_mensile ? `€${v.canone_mensile}/mese · ` : ""}{v.durata_mesi ? `${v.durata_mesi} mesi · ` : ""}{v.km_annui ? `${v.km_annui} km/anno` : ""}
            </p>
          </SummaryBox>
        )}
        <div className="bg-[#FFF7E8] border border-[#E8D3A0] rounded-xl p-4 text-xs text-[#8B6100] leading-relaxed flex gap-3">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            Dopo l'invio un consulente Colucci ti contatterà per verificare i dati. Quando la pratica sarà pronta, riceverai un'email con il link sicuro YouSign per la firma elettronica dei documenti Ayvens (MLA, informativa SIC, stato d'uso).
          </div>
        </div>
      </div>
    </div>
  );
}

function SuccessView({ result }) {
  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-5 text-center" data-testid="credito-success">
          <div className="w-16 h-16 rounded-full bg-[#163321] flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-3xl text-[#0F1F14] font-medium mb-3">Pratica inviata con successo</h1>
          <p className="text-sm text-[#4A5E51] mb-6 leading-relaxed">
            Riferimento pratica: <strong className="text-[#163321] tracking-widest">{result.pratica_id}</strong>
          </p>

          {/* CTA Verifica moduli pre-firma */}
          <div className="bg-[#163321] text-white rounded-2xl p-6 text-left mb-5">
            <div className="flex items-start gap-3 mb-4">
              <Sparkles className="w-6 h-6 text-[#D97B58] shrink-0 mt-0.5" />
              <div>
                <p className="font-heading text-lg font-semibold mb-1">Verifica i moduli precompilati</p>
                <p className="text-sm text-white/80 leading-relaxed">
                  Abbiamo già compilato i moduli Ayvens con i tuoi dati. Controllali adesso — se tutto è corretto, li passiamo direttamente a YouSign per la firma elettronica.
                </p>
              </div>
            </div>
            <Link
              to={`/credito/pratica/${result.pratica_id}/verifica?token=${encodeURIComponent(result.public_token)}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D97B58] text-white text-sm font-semibold hover:bg-[#c56a48] transition-colors"
              data-testid="btn-verifica-moduli"
            >
              Verifica i moduli ora <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="bg-white border border-[#E5E2DB] rounded-2xl p-5 text-left mb-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#D97B58] shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-[#0F1F14] mb-1">Prossimi passi</p>
                <ol className="text-xs text-[#4A5E51] space-y-1.5 list-decimal list-inside leading-relaxed">
                  <li>Verifica i moduli precompilati (MLA, SEPA, Servizi Aggiuntivi).</li>
                  <li>Conferma l'esattezza dei dati.</li>
                  <li>Firma elettronicamente tramite <strong>YouSign</strong> (link sicuro via email).</li>
                  <li>Ayvens emette la lettera di offerta e avvia l'ordine.</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/noleggio" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#E5E2DB] text-[#4A5E51] text-xs font-semibold hover:border-[#163321] transition-colors">
              <CarFront className="w-4 h-4" /> Catalogo
            </Link>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#E5E2DB] text-[#4A5E51] text-xs font-semibold hover:border-[#163321] transition-colors">
              Home
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

// ============================= UI PARTS =============================
function Field({ label, children, required, wide }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <Label className="text-xs font-semibold text-[#0F1F14] mb-1.5 block">
        {label}{required && <span className="text-[#D97B58] ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}

function SummaryBox({ title, icon: Icon, children }) {
  return (
    <div className="bg-[#F5F8F6] border border-[#163321]/15 rounded-xl p-4">
      <p className="text-[10px] uppercase tracking-widest font-bold text-[#163321] mb-2 flex items-center gap-1.5">
        <Icon className="w-3 h-3" /> {title}
      </p>
      {children}
    </div>
  );
}

// ============================================================================
// AUTO-EXTRACT AI — Carica documento e popola automaticamente i campi
// ============================================================================
function AutoExtractBox({ form, setForm }) {
  const [uploading, setUploading] = useState(false);
  const [extracted, setExtracted] = useState(null);

  const isPrivato = form.tipologia === "privato";

  // Riconosce il tipo documento: per privati la CI/Patente, per imprese la Visura Camerale.
  // L'utente può comunque caricare qualsiasi documento e l'AI prova a estrarre.
  const docType = isPrivato ? "identita" : "visura";
  const docLabel = isPrivato
    ? "📷 Carica la Carta d'Identità o Patente"
    : "📷 Carica la Visura Camerale o atto costitutivo";

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("File troppo grande (max 8 MB)"); return; }
    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      toast.error("Carica un'immagine (JPG/PNG) o un PDF"); return;
    }
    setUploading(true);
    try {
      // Per i PDF non possiamo fare extract diretto da AI vision: dobbiamo accettare solo immagini.
      // Per ora trattiamo PDF/img uniformemente convertendo in base64.
      if (file.type === "application/pdf") {
        toast.error("Per ora supportiamo solo immagini (JPG/PNG). Per i PDF compila a mano.");
        setUploading(false); return;
      }
      const reader = new FileReader();
      const b64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const r = await axios.post(`${API}/documenti/analizza`, { image_base64: b64, tipo_documento: docType });
      const d = r.data?.dati || {};

      // Popola i campi senza sovrascrivere ciò che l'utente ha già inserito a mano
      setForm(f => {
        const next = { ...f, anagrafica: { ...f.anagrafica }, privato: { ...f.privato } };
        if (isPrivato) {
          // CI/Patente → anagrafica + privato
          const nomeCompleto = `${d.nome || ""} ${d.cognome || ""}`.trim();
          if (nomeCompleto && !f.anagrafica.denominazione) next.anagrafica.denominazione = nomeCompleto;
          if (d.codice_fiscale && !f.anagrafica.cf_piva) next.anagrafica.cf_piva = d.codice_fiscale.toUpperCase();
          const ind = d.indirizzo || d.indirizzo_residenza;
          if (ind && !f.anagrafica.indirizzo) next.anagrafica.indirizzo = ind;
          if (d.cap && !f.anagrafica.cap) next.anagrafica.cap = String(d.cap).replace(/\D/g, "").slice(0, 5);
          if ((d.comune || d.comune_residenza) && !f.anagrafica.citta) next.anagrafica.citta = d.comune || d.comune_residenza;
          if (d.provincia && !f.anagrafica.provincia) next.anagrafica.provincia = String(d.provincia).toUpperCase().slice(0, 2);
          // Privato extra
          if (d.data_nascita && !f.privato.data_nascita) next.privato.data_nascita = d.data_nascita;
          if (d.luogo_nascita && !f.privato.luogo_nascita) next.privato.luogo_nascita = d.luogo_nascita;
          if (d.numero_documento && !f.privato.documento_numero) next.privato.documento_numero = d.numero_documento;
          if (d.tipo_documento && !f.privato.documento_tipo) next.privato.documento_tipo = d.tipo_documento;
        } else {
          // Visura camerale → anagrafica azienda + dati impresa
          if (d.ragione_sociale && !f.anagrafica.denominazione) next.anagrafica.denominazione = d.ragione_sociale;
          if (d.partita_iva && !f.anagrafica.cf_piva) next.anagrafica.cf_piva = String(d.partita_iva).replace(/\D/g, "").slice(0, 11);
          if (d.sede_legale && !f.anagrafica.indirizzo) next.anagrafica.indirizzo = d.sede_legale;
          if (d.cap && !f.anagrafica.cap) next.anagrafica.cap = String(d.cap).replace(/\D/g, "").slice(0, 5);
          if (d.comune && !f.anagrafica.citta) next.anagrafica.citta = d.comune;
          if (d.provincia && !f.anagrafica.provincia) next.anagrafica.provincia = String(d.provincia).toUpperCase().slice(0, 2);
          if (d.pec && !f.anagrafica.pec) next.anagrafica.pec = d.pec;
          // Impresa extra
          next.impresa = { ...f.impresa };
          if (d.forma_giuridica && !f.impresa.forma_giuridica) next.impresa.forma_giuridica = d.forma_giuridica;
          if (d.numero_rea && !f.impresa.num_rea) next.impresa.num_rea = d.numero_rea;
          if (d.camera_commercio && !f.impresa.camera_commercio) next.impresa.camera_commercio = d.camera_commercio;
          if (d.codice_ateco && !f.impresa.settore_ateco) next.impresa.settore_ateco = d.codice_ateco;
          if (d.anno_costituzione && !f.impresa.anno_costituzione) next.impresa.anno_costituzione = String(d.anno_costituzione);
        }
        return next;
      });
      setExtracted({ tipo: docType, fields_count: Object.keys(d).filter(k => d[k]).length });
      toast.success("✨ Dati estratti! Verifica e correggi se necessario.");
    } catch (e) {
      console.warn("[AutoExtract]", e?.response?.data?.detail || e?.message);
      toast.error(e?.response?.data?.detail || "Estrazione AI non riuscita. Compila manualmente i campi.");
    } finally { setUploading(false); }
  };

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-[#163321]/5 border border-emerald-300 rounded-xl p-4 mb-5" data-testid="autoextract-box">
      <div className="flex items-start gap-3">
        <Sparkles className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-emerald-900 mb-0.5">Compilazione automatica con AI</p>
          <p className="text-[11px] text-emerald-800/80 leading-relaxed mb-3">
            {extracted
              ? <>✓ <strong>{extracted.fields_count} campi</strong> compilati automaticamente. Verifica i dati qui sotto e correggi se necessario.</>
              : <>Per velocizzare la compilazione, carica una <strong>foto</strong> {isPrivato ? "della tua Carta d'Identità o Patente" : "della Visura Camerale"} (JPG/PNG). I dati verranno letti e inseriti automaticamente nei campi.</>}
          </p>
          <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-full cursor-pointer text-xs font-semibold transition ${uploading ? "bg-gray-300 text-gray-600" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`} data-testid="autoextract-upload-label">
            {uploading ? "Estrazione in corso…" : (extracted ? "Carica un altro documento" : docLabel)}
            <input type="file" accept="image/jpeg,image/png,image/jpg" className="hidden" onChange={e => handleFile(e.target.files?.[0])} disabled={uploading} data-testid="autoextract-file-input" />
          </label>
          <p className="text-[10px] text-emerald-700/70 mt-2">L'immagine viene analizzata in tempo reale e <strong>non viene salvata</strong>. I dati estratti possono essere modificati prima dell'invio.</p>
        </div>
      </div>
    </div>
  );
}

